/* eslint-disable no-unreachable */
import { insideProviderIframe, getHost } from '@/utils/providerIframe';
import isMobile from '@/utils/mobile-check';
import { Route } from 'vue-router';
import { isEquivalent } from './is-equivalent';
import isRemoteLoading from './remote-loading';

declare global {
  interface Window {
    zE?: (target: string | Object, channel?: string, config?: any) => void;
    $zopim?: any;
  }
}

const scriptID = 'ze-snippet';
export const widgetID = 'launcher';
const key = process.env.VUE_APP_ZENDESK_WIDGET_KEY;

let injected = false;

export function injectZendeskScript(bottomCSS?: string) {
  const scriptEl = document.getElementById(scriptID);
  const widget = document.getElementById(widgetID);

  injected = true;

  if (scriptEl && widget) {
    widget.style.opacity = '1';
    widget.style.display = 'block';

    if (bottomCSS) widget.style.bottom = bottomCSS;
  } else if (!scriptEl) {
    const script = document.createElement('script');
    script.id = scriptID;
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;

    document.body.appendChild(script);
  }
}

export function removeZendeskScript() {
  const widget = document.getElementById(widgetID);

  if (widget) {
    widget.style.opacity = '0';
    widget.style.display = 'none';

    return true;
  }

  return false;
}

export function embedZendeskScript() {
  const widget = document.getElementById(widgetID) as any;

  if (widget) {
    widget.style.opacity = '1';
    widget.style.display = 'block';

    return true;
  }

  return false;
}

let hideZendeskInterval: any = null;
export function hideZendeskWidget() {
  clearInterval(hideZendeskInterval);

  hideZendeskInterval = setInterval(() => {
    if (window && window.zE) {
      return window.zE(() => {
        if (removeZendeskScript()) {
          clearInterval(hideZendeskInterval);
        }
      });
    }
  });

  return hideZendeskInterval;
}

let showZendeskInterval: any = null;
export function showZendeskWidget() {
  clearInterval(showZendeskInterval);

  showZendeskInterval = setInterval(() => {
    if (window && window.zE) {
      return window.zE(() => {
        if (embedZendeskScript()) {
          clearInterval(showZendeskInterval);
        }
      });
    }
  }, 60);

  return showZendeskInterval;
}

let moveZendeskInterval: any = null;
export function moveZendeskWidget(direction: 'bl' | 'br' | 'tl' | 'tr') {
  clearInterval(moveZendeskInterval);

  moveZendeskInterval = setInterval(() => {
    clearInterval(moveZendeskInterval);
    if (window && window.zE) {
      return window.zE(() => {
        if (window.$zopim && window.$zopim.livechat) {
          window.$zopim(() => {
            window.$zopim.livechat.button.setPosition(direction);

            if (insideProviderIframe() && getHost('wix') && !isMobile()) {
              window.$zopim.livechat.button.setOffsetVertical('18px');

              window.$zopim.livechat.button.setOffsetHorizontal(
                direction === 'br' ? '90px' : '0px'
              );
            }
          });
        }
      });
    }
  }, 200);

  return moveZendeskInterval;
}

export function changeZendeskSettings() {
  /**
   * Change Zendesk Widget position in WIX page
   */
  if (insideProviderIframe() && getHost('wix')) {
    const zEInterval = setInterval(() => {
      if (window && window.zE) {
        clearInterval(zEInterval);
        window.zE(() => {
          if (window.$zopim && window.$zopim.livechat) {
            window.$zopim(() => {
              window.$zopim.livechat.setGreetings({
                online: 'Twism Support',
                offline: 'Twism Support'
              });
            });
          }
        });
      }
    }, 200);
  }
}

export function getDefaultSettings() {
  if (getHost('wix')) {
    return {
      position: 'br',
      offsetVertical: '0px',
      offsetHorizontal: '0px',
      greetings: {
        online: 'Twism Support',
        offline: 'Twism Support'
      }
    };
  }

  return {
    position: 'br',
    offsetVertical: '0px',
    offsetHorizontal: '0px',
    greetings: undefined
  };
}

export function getDirectionZendeskWidget() {
  const element = document.querySelector(`body > div #${widgetID}`);

  let direction = '';

  if (!window.zE || !element) return direction;

  const viewportWidth = window.innerWidth;
  const { left: leftPos } = element.getBoundingClientRect();

  if (leftPos < viewportWidth / 2) {
    direction = 'left';
  }

  if (leftPos > viewportWidth / 2) {
    direction = 'right';
  }

  return direction;
}

export function resetZendkeskSettings() {
  return setZendeskSettings({}, isMobile() ? 'mobile' : 'desktop');
}

function setMobileZendeskSettings(settings: any) {
  return setZendeskSettings(settings, 'mobile');
}
function setDesktopZendeskSettings(settings: any) {
  return setZendeskSettings(settings, 'desktop');
}
function setProviderZendeskSettings(settings: any) {
  return setZendeskSettings(settings, 'provider');
}

let zendeskSettingsInterval: any = null;
let lastSettings: any;

function setZendeskSettings(
  customSettings: any = {},
  target: 'mobile' | 'desktop' | 'provider'
) {
  const settings = {
    ...getDefaultSettings(),
    ...customSettings
  };

  if (isEquivalent(settings, lastSettings)) {
    return;
  }

  clearInterval(zendeskSettingsInterval);

  lastSettings = {
    ...settings
  };

  zendeskSettingsInterval = setInterval(() => {
    if (window && window.zE) {
      return window.zE(() => {
        if (window.$zopim && window.$zopim.livechat) {
          window.$zopim(() => {
            if (settings.disabled) {
              if (removeZendeskScript()) {
                clearInterval(zendeskSettingsInterval);
              }

              return;
            }

            embedZendeskScript();

            if (settings.position) {
              window.$zopim.livechat.button.setPosition(settings.position);
            }

            if (settings.offsetVertical && target !== 'mobile') {
              window.$zopim.livechat.button.setOffsetVertical(
                settings.offsetVertical
              );
            } else {
              window.$zopim.livechat.button.setOffsetVerticalMobile(
                settings.offsetVertical
              );
            }

            if (settings.offsetHorizontal && target !== 'mobile') {
              window.$zopim.livechat.button.setOffsetHorizontal(
                settings.offsetHorizontal
              );
            } else {
              window.$zopim.livechat.button.setOffsetHorizontalMobile(
                settings.offsetHorizontal
              );
            }

            if (settings.greetings) {
              window.$zopim.livechat.setGreetings(settings.greetings);
            }

            clearInterval(zendeskSettingsInterval);
          });
        }
      });
    }
  }, 200);

  return moveZendeskInterval;
}

// function hasProviderSpecificSettings(settings: any): boolean {
//   const provider = getProvider();
//   return settings.all || (provider && settings[provider]);
// }

export function handleZendeskByRoute(route: Route) {
  // test if its remote, to avoid inject zendesk
  if (isRemoteLoading()) return false;

  if (!injected) {
    injectZendeskScript();
  }

  if (!route.meta || !route.meta.zendesk) {
    return resetZendkeskSettings();
  }

  if (route.meta.zendesk.disabled === true) {
    return hideZendeskWidget();
  }

  // if (insideProviderIframe() && route.meta.zendesk.providers) {
  //   return setProviderZendeskSettings(route.meta.zendesk.providers);
  // }

  if (route.meta.zendesk.mobile && isMobile()) {
    return setMobileZendeskSettings(route.meta.zendesk.mobile);
  }

  if (route.meta.zendesk.desktop) {
    return setDesktopZendeskSettings(route.meta.zendesk.desktop);
  }

  return resetZendkeskSettings();
}
